// One-line "where you stand + what to do next" prompt under the meta strip.
//
// Replaces the deleted progress widget. The first attempt at a leaner
// header dropped every signal — but the homeowner still needs to know
// (a) is anything wrong with my timeline, (b) what's the next thing I
// can do to make my case stronger.
//
// Design rules (from the user's pushback on the prior iteration):
//   - Positive framing only. No "missing", "incomplete", "X to go".
//     Adding evidence is how the user STRENGTHENS the case, not how they
//     fix a defect in our system.
//   - One sentence. The user already has tabs, a chat agent, and a Data
//     tab — those are the action surfaces. The header is not a control
//     panel. It can suggest ONE thing.
//   - Conditional. If the timeline check is fine and the slots are full,
//     the header sits quiet — the case-detail page works without us
//     yelling at the user.
//   - No bars, no percentages, no popovers. Those are the SaaS-dashboard
//     register that the prior iteration hit. We're a story product.
//
// Action priority (top wins):
//   1. Chronology conflict — terracotta warning line.
//   2. Missing the agreement anchor → "Add a contract or quote …"
//   3. Missing payment proof → "Add a payment receipt …"
//   4. Missing defect/abandonment narrative → suggest the chat (it's a
//      narrative thing, not a document thing).
//   5. Slots full but finer facts unfilled → "Polish the details in Data
//      when you have a moment".
//   6. Nothing to nudge → render null.

import { useCaseFacts } from '../case-facts';
import { m } from '../paraglide/messages';
import { useAutoRecheckConsistency, useProgress } from '../progress';

type Tab = 'timeline' | 'data' | 'sources' | 'artifacts';

type Action = {
  text: string;
  tab: Tab;
};

type Props = {
  caseId: string;
  onJumpToTab?: (tab: Tab) => void;
};

export function CaseSituation({ caseId, onJumpToTab }: Props) {
  const facts = useCaseFacts(caseId);
  const progress = useProgress(caseId);
  // Keep the chronology verdict fresh as the timeline mutates. The hook
  // is debounced + deduped on its end; cheap to mount here.
  useAutoRecheckConsistency(caseId, progress.data);

  if (!progress.data) return null;

  const consistency = progress.data.consistency;
  const showWarn = !!consistency && !consistency.ok && !consistency.stale;

  if (showWarn && consistency) {
    return (
      <p className="case-situation case-situation-warn">
        <span aria-hidden="true">⚠ </span>
        {consistency.explanation}{' '}
        <button
          type="button"
          className="situation-action"
          onClick={() => onJumpToTab?.('timeline')}
        >
          {m.situationReviewTimeline()} →
        </button>
      </p>
    );
  }

  const action = pickAction(progress.data, facts.data?.completeness?.ratio ?? 0);
  if (!action) return null;

  return (
    <p className="case-situation">
      <button type="button" className="situation-action" onClick={() => onJumpToTab?.(action.tab)}>
        {action.text} →
      </button>
    </p>
  );
}

function pickAction(
  progress: { slots: { [k: string]: boolean }; eventCount: number },
  factsRatio: number,
): Action | null {
  const s = progress.slots;
  if (!s.hasAgreement) {
    return { text: m.situationAddAgreement(), tab: 'sources' };
  }
  if (!s.hasPayment) {
    return { text: m.situationAddPayment(), tab: 'sources' };
  }
  if (!s.hasDefect) {
    // The defect is a narrative — chat is the right surface, but the
    // case-detail layout has the chat permanently in the left pane, so
    // there's no "tab" to switch to. We still nudge the user with
    // language that points them at the chat; the click is a no-op.
    return { text: m.situationAddDefect(), tab: 'timeline' };
  }
  if (!s.hasAbandonmentOrMissedDeadline) {
    return { text: m.situationAddAbandonment(), tab: 'timeline' };
  }
  // Slots are full. If facts are still sparse, point to Data.
  if (factsRatio < 0.8) {
    return { text: m.situationPolishDetails(), tab: 'data' };
  }
  return null;
}
